import { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useScope } from './ScopeContext';
import type { NursingHome } from './types';

interface NursingHomeContextProps {
  selectedNursingHome: NursingHome | undefined;
  initNursingHome: (nursingHome: NursingHome) => Promise<void>;
  nursingHomeList: NursingHome[];
  setNursingHomeList: (nursingHomeList: NursingHome[]) => void;
}

/**
 * Compatibility wrapper over {@link useScope} for the call sites that predate
 * the scope selector.
 *
 * @deprecated Read the nursing home from `useScope()` instead.
 *
 * @example
 * const { selectedNursingHome } = useNursingHomeContext();
 */
export const useNursingHomeContext = (): NursingHomeContextProps => {
  const {
    selectedNursingHome,
    nursingHomeList,
    selectNursingHome,
    setNursingHomeList,
  } = useScope();

  return useMemo(
    () => ({
      selectedNursingHome,
      nursingHomeList,
      initNursingHome: selectNursingHome,
      setNursingHomeList,
    }),
    [
      selectedNursingHome,
      nursingHomeList,
      selectNursingHome,
      setNursingHomeList,
    ]
  );
};

/**
 * As {@link useNursingHomeContext}, but suspends until a nursing home is
 * selected so consumers can treat it as always present.
 *
 * @deprecated Read the nursing home from `useScope()` instead.
 *
 * @example
 * const { selectedNursingHome } = useNursingHomeContextWithSuspense();
 */
export const useNursingHomeContextWithSuspense = (): Omit<
  NursingHomeContextProps,
  'selectedNursingHome'
> & {
  selectedNursingHome: NursingHome;
} => {
  const context = useNursingHomeContext();
  const [isWaiting, setIsWaiting] = useState(
    () => !context.selectedNursingHome
  );
  // Add a counter to track suspension attempts
  const suspensionCounter = useRef(0);

  // Update isWaiting when selectedNursingHome changes
  useEffect(() => {
    if (context.selectedNursingHome && isWaiting) {
      setIsWaiting(false);
    }
  }, [context.selectedNursingHome, isWaiting]);

  if (isWaiting) {
    // Increment the counter before suspending
    suspensionCounter.current += 1;

    // Log to Sentry if suspended too many times
    if (suspensionCounter.current > 5) {
      Sentry.captureMessage(
        `NursingHomeContextWithSuspense suspended ${suspensionCounter.current} times`,
        {
          level: 'warning',
          tags: {
            component: 'NursingHomeContextWithSuspense',
          },
          extra: {
            suspensionCounter: suspensionCounter.current,
          },
        }
      );
    }

    throw new Promise((resolve) => {
      setTimeout(resolve, 50);
    });
  }

  return {
    ...context,
    selectedNursingHome: context.selectedNursingHome as NursingHome,
  };
};
